import { Mutations, Actions, Module } from 'vuex-smart-module'

import { loading as loadingModule, LoadingGetters } from '@/store/modules/loading'
import { ApiError, SecurityService, ClientUser, UserContactsChangeRequest, UserService, ClaimContactData } from '@/api'

import { mock } from '@/tools'

const loading = loadingModule.clone()

export class SecurityState {
  error: string = null
  user?: ClientUser = null
  cabinetAccess = false
}

export class UserContacts<D extends ClaimContactData> {
  constructor(
    userContacts: D,
    public factAddress = userContacts?.factAddress,
    public mobilePhone = userContacts?.mobilePhone,
    public phone = userContacts?.phone,
    public email = userContacts?.email,
    public emailAccept = userContacts?.emailAccept,
    public temporaryRegAddress = userContacts?.temporaryRegAddress
  ) {}

  get toSave() {
    return {
      factAddress: this.factAddress || '',
      mobilePhone: this.mobilePhone,
      phone: this.phone || '',
      email: this.email,
      emailAccept: this.emailAccept,
      temporaryRegAddress: this.temporaryRegAddress || '',
    }
  }
}

export class SecurityGetters extends LoadingGetters<SecurityActions, SecurityState> {
  get isUser() {
    return Boolean(this.state.user)
  }
  get isError() {
    return Boolean(this.state.error)
  }

  get firstAndMiddleName() {
    return `${this.state.user?.mainData?.firstName || ''}${this.state.user?.mainData?.middleName ? ' ' : ''}${
      this.state.user?.mainData?.middleName || ''
    }`
  }
  get userToForm() {
    return {
      snils: this.state.user?.mainData?.snils ?? null,
      oms: this.state.user?.mainData?.oms ?? null,
      firstName: this.state.user?.mainData?.firstName ?? null,
      middleName: this.state.user?.mainData?.middleName ?? null,
      lastName: this.state.user?.mainData?.lastName ?? null,
      gender: this.state.user?.mainData?.gender ?? null,
      birthDate: this.state.user?.mainData?.birthDate.replace(/(\d+)\.(\d+)\.(\d+)/, '$3-$2-$1') ?? null,
      birthPlace: this.state.user?.mainData?.birthPlace ?? null,
      docType: this.state.user?.document?.docType.toString() ?? null,
      series: this.state.user?.document?.series ?? null,
      number: this.state.user?.document?.number ?? null,
      issuedBy: this.state.user?.document?.issuedBy ?? null,
      issueDate: this.state.user?.document?.issueDate.replace(/(\d+)\.(\d+)\.(\d+)/, '$3-$2-$1') ?? null,
      issueCode: this.state.user?.document?.issueCode ?? null,
      regAddress: this.state.user?.contacts?.regAddress ?? null,
    }
  }
  get userContacts() {
    return {
      factAddress: this.state.user?.contacts?.factAddress,
      mobilePhone: this.state.user?.contacts?.mobilePhone,
      phone: this.state.user?.contacts?.phone,
      email: this.state.user?.contacts?.email,
      emailAccept: this.state.user?.contacts?.emailAccept,
      temporaryRegAddress: this.state.user?.contacts?.temporaryRegAddress,
      regAddress: this.state.user?.contacts?.regAddress,
    }
  }

  get isChangeUserContacts() {
    return this.state.loading.loaders.changeUserContacts
  }
  get isUserGet() {
    return this.state.loading.loaders.getUser
  }
  get isLogoutSend() {
    return this.state.loading.loaders.getUser
  }
  // get isRecoverPasswordByLinkGet() {
  //   return this.state.loading.loaders.recoverPasswordByLink
  // }
}

export class SecurityMutations extends Mutations<SecurityState> {
  setUser(user: ClientUser) {
    if (user) this.state.error = null
    this.state.user = user
  }

  setError(error = 'Не удалось получить данные пользователя') {
    this.state.error = error
  }

  setCabinetAccess(access: boolean) {
    this.state.cabinetAccess = access
  }
}

export class SecurityActions extends Actions<SecurityState, SecurityGetters, SecurityMutations, SecurityActions> {
  async getUser() {
    try {
      const user = await UserService.getUser()
      this.mutations.setUser(user)
    } catch (error) {
      this.mutations.setUser(null)
      // this.mutations.setError()
      if (error instanceof ApiError)
        if (error.status === 403) mock(error)
        else throw error
    }
  }

  async checkCabinetAccess() {
    if (this.state.cabinetAccess) return this.state.cabinetAccess
    else {
      const result = await SecurityService.checkCabinetAccess()
      if (result) this.mutations.setCabinetAccess(result)
      return result
    }
  }

  async changeUserContacts(requestBody: UserContactsChangeRequest) {
    await UserService.changeContacts(requestBody)
  }

  async logout() {
    await SecurityService.logout()
    this.mutations.setUser(null)
  }
}

export type SecurityModules = {
  loading: typeof loading
}

export const security = new Module<SecurityState, SecurityGetters, SecurityMutations, SecurityActions, SecurityModules>(
  {
    modules: { loading },
    state: SecurityState,
    getters: SecurityGetters,
    mutations: SecurityMutations,
    actions: SecurityActions,
  }
)
